import { useCallback } from 'react';
import queryString from 'query-string';
import { useRouter } from '@/hooks';

interface UrlParams {
  assembly?: string;
  overviewTab?: string;
  poi?: string;
  /** Kebab-case POI template tab id in review panel (matches `toKebabCase(template.name)`). Omitted = All. */
  reviewPoiTab?: string;
  [key: string]: string | string[] | undefined;
}

export const useInsightsPageParams = () => {
  const router = useRouter();

  const getCurrentParams = useCallback((): UrlParams => {
    return queryString.parse(window.location.search, { arrayFormat: 'comma' }) as UrlParams;
  }, []);

  const updateParams = useCallback(
    (newParams: Partial<UrlParams>) => {
      const currentParams = getCurrentParams();
      const updatedParams = { ...currentParams, ...newParams };

      router.push({
        pathname: router.pathname,
        query: updatedParams as Record<string, string>,
      });
    },
    [router, getCurrentParams]
  );

  const removeParams = useCallback(
    (paramsToRemove: string[]) => {
      const currentParams = getCurrentParams();
      const updatedParams = { ...currentParams };

      paramsToRemove.forEach((param) => {
        delete updatedParams[param];
      });

      router.push({
        pathname: router.pathname,
        query: updatedParams as Record<string, string>,
      });
    },
    [router, getCurrentParams]
  );

  const setAssembly = useCallback(
    (assembly: string, overviewTab: string = '1') => {
      updateParams({ assembly, overviewTab });
    },
    [updateParams]
  );

  const setOverviewTab = useCallback(
    (overviewTab: string) => {
      updateParams({ overviewTab });
    },
    [updateParams]
  );

  const setPoi = useCallback(
    (poi: string) => {
      updateParams({ poi });
    },
    [updateParams]
  );

  const setReviewPoiTab = useCallback(
    (tabKebabId: string | undefined) => {
      if (!tabKebabId) {
        removeParams(['reviewPoiTab']);
      } else {
        updateParams({ reviewPoiTab: tabKebabId });
      }
    },
    [updateParams, removeParams]
  );

  const clearAssembly = useCallback(() => {
    removeParams([
      'selectedAssemblyName',
      'overviewTab',
      'selectedAssemblyId',
      'assembly',
      'reviewPoiTab',
    ]);
  }, [removeParams]);

  const clearPoi = useCallback(() => {
    removeParams(['poi']);
  }, [removeParams]);

  return {
    getCurrentParams,
    updateParams,
    removeParams,
    setAssembly,
    setOverviewTab,
    setPoi,
    setReviewPoiTab,
    clearAssembly,
    clearPoi,
  };
};
